import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { SettingsLayout } from "./components/layout/SettingsLayout";
import { requireAdmin, requireAuth } from "./lib/route-guards";
import { RootLayout } from "./routes/__root";
import { DashboardPage } from "./routes/dashboard/index";
import { ProfilePage } from "./routes/dashboard/profile";
import { SettingsAuditPage } from "./routes/dashboard/settings/audit";
import { SettingsEmailPage } from "./routes/dashboard/settings/email";
import { SettingsSystemPage } from "./routes/dashboard/settings/system";
import { SettingsUsersPage } from "./routes/dashboard/settings/users";
import { LoginPage } from "./routes/login";
import { SignupPage } from "./routes/signup";

const rootRoute = createRootRoute({ component: RootLayout });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/login" });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignupPage,
});

function DashboardLayoutRoute() {
  return (
    <DashboardLayout>
      <Outlet />
    </DashboardLayout>
  );
}

const dashboardLayout = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  beforeLoad: requireAuth,
  component: DashboardLayoutRoute,
});

const dashboardIndex = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/",
  component: DashboardPage,
});

const profileRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/profile",
  component: ProfilePage,
});

function SettingsLayoutRoute() {
  return (
    <SettingsLayout>
      <Outlet />
    </SettingsLayout>
  );
}

const settingsLayout = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/settings",
  beforeLoad: async (ctx) => {
    await requireAdmin();
    if (ctx.location.pathname === "/dashboard/settings") {
      throw redirect({ to: "/dashboard/settings/users" });
    }
  },
  component: SettingsLayoutRoute,
});

const settingsUsersRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/users",
  component: SettingsUsersPage,
});

const settingsSystemRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/system",
  component: SettingsSystemPage,
});

const settingsEmailRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/email",
  component: SettingsEmailPage,
});

const settingsAuditRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/audit",
  component: SettingsAuditPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  dashboardLayout.addChildren([
    dashboardIndex,
    profileRoute,
    settingsLayout.addChildren([
      settingsUsersRoute,
      settingsSystemRoute,
      settingsEmailRoute,
      settingsAuditRoute,
    ]),
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
